// QR Kiosk v2 — UI-only slice (tier, account sheet, offline, per-surface
// staleness + load-state, validation summary). REUSES publicSlice data thunks
// unchanged — imported for extraReducers matching only, never re-declared.
// publicSlice itself is byte-untouched; import direction kioskV2Slice → publicSlice
// is cycle-safe (publicSlice never imports v2).
//
// Load-state convention (normative for all v2 data pages, KV2-5): publicSlice
// `loading`/`error` are single shared flags that NO v2 page trusts. Instead each
// surface listens here — fulfilled writes `loadState[surface].loadedKey` (+ clears
// failedKey) and `lastLoadedAt[surface]`; rejected writes `failedKey` (preserving
// loadedKey — the cache stays valid). `useSurfaceState(surface, routeKey)` reads
// that map and drives the 4-branch render tree: data && failed → cached-dimmed ·
// data → normal · failed → full error · else → skeleton (idle+pending). This kills
// the one-frame cold-mount error flash and the empty-array scope ambiguity.
// (ticket §1.4 / §4.3 / §5.1 bug #10)
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getMachine,
  getCustomer,
  getServiceReports,
  getServiceReport,
  getDocuments,
} from "../publicSlice";
// Type-only imports (cycle-safe, MUI-free): ChatMessage is a plain-JSON shape;
// KioskDraftTicket lives in the KV2-6 midas contract. Both elide at runtime.
import type { ChatMessage } from "../../shared/chatBot/models/chatBotModels";
import type { KioskDraftTicket } from "./midas/draftTicketContract";
// KV2-7 — task thunks listened for surface settle only (import direction
// kioskV2Slice → kioskTasksSlice; kioskTasksSlice imports NOTHING back, cycle-safe).
import {
  getKioskTasks,
  getKioskTask,
  startKioskTask,
  holdKioskTask,
  resumeKioskTask,
  doneKioskTask,
} from "./tasks/kioskTasksSlice";

export type KioskTier = "anon" | "guest" | "login";
export type KioskSurface =
  | "machine"
  | "customer"
  | "serviceReports"
  | "serviceReport"
  | "documents"
  | "tasks"
  | "task";

export interface KioskValidationSummary {
  // reserved for KV2-3; shape may be extended ADDITIVELY there
  count: number;
  message: string;
}

export interface SurfaceLoadState {
  /** normalized (lowercased) scope key of the last FULFILLED load of this surface */
  loadedKey?: string;
  /** normalized scope key of the last REJECTED load; cleared on the next fulfilled */
  failedKey?: string;
}

// KV2-6 — fullscreen-MIDAS session state. Moving the chat thread out of component
// state is the "state preserved on back" delta (§3-T1): closing/reopening the
// takeover, and dashboard↔page navigation, no longer destroys the conversation.
// Everything stored is plain JSON (ChatMessage fields + object-URL strings), so
// the slice stays serializable. One session per machine; reset on machine switch.
export interface KioskMidasSession {
  machineId: string; // GUID this session is bound to
  threadId: string | null; // null = thread create failed (non-fatal, v1 parity)
  messages: ChatMessage[]; // committed turns only — transient stream state stays component-local
  drafts: Record<string, KioskDraftTicket>; // messageId → normalized draft (carrier B lands here; carrier A reads at render)
  createdDrafts: Record<string, string>; // messageId → created-at ISO; presence = card shows "created", Create gone
}

export interface KioskV2State {
  tier: KioskTier; // "login" until KV2-8/9
  accountSheetOpen: boolean;
  offline: boolean; // written by KV2-5's listener
  lastLoadedAt: Partial<Record<KioskSurface, number>>; // epoch ms per surface (cached-at readouts, KV2-5)
  loadState: Partial<Record<KioskSurface, SurfaceLoadState>>; // per-surface fulfilled/rejected keys (KV2-5)
  validationSummary: KioskValidationSummary | null; // written by KV2-3
  midasSession: KioskMidasSession | null; // written by KV2-6 (null = no session yet)
}

const initialState: KioskV2State = {
  tier: "login",
  accountSheetOpen: false,
  offline: false,
  lastLoadedAt: {},
  loadState: {},
  validationSummary: null,
  midasSession: null,
};

// One place, five call pairs. fulfilled: stamp lastLoadedAt (kept from KV2-0) + set
// loadedKey AND clear failedKey (a success flips a cached-dimmed surface back to
// normal). rejected: set failedKey, preserve loadedKey (cache still valid). Keys are
// lowercased at write time; empty/undefined arg → skip the load-state write (defensive;
// cannot happen from the committed call sites). Values are plain strings/numbers.
function settleFulfilled(state: KioskV2State, surface: KioskSurface, key?: string) {
  state.lastLoadedAt[surface] = Date.now();
  const k = key?.toLowerCase();
  if (!k) return;
  state.loadState[surface] = { loadedKey: k };
}

function settleRejected(state: KioskV2State, surface: KioskSurface, key?: string) {
  const k = key?.toLowerCase();
  if (!k) return;
  state.loadState[surface] = { ...state.loadState[surface], failedKey: k };
}

const kioskV2Slice = createSlice({
  name: "kioskV2",
  initialState,
  reducers: {
    setTier(state, action: PayloadAction<KioskTier>) {
      state.tier = action.payload;
    },
    openAccountSheet(state) {
      state.accountSheetOpen = true;
    },
    closeAccountSheet(state) {
      state.accountSheetOpen = false;
    },
    setOffline(state, action: PayloadAction<boolean>) {
      state.offline = action.payload;
    },
    setValidationSummary(state, action: PayloadAction<KioskValidationSummary | null>) {
      state.validationSummary = action.payload;
    },
    // ── KV2-6 MIDAS session (all additive) ──
    // Restore path: same machine → no-op (keep the conversation); different/absent
    // machine → fresh session (no cross-machine thread bleed).
    midasSessionStart(state, action: PayloadAction<string>) {
      if (!state.midasSession || state.midasSession.machineId !== action.payload) {
        state.midasSession = {
          machineId: action.payload,
          threadId: null,
          messages: [],
          drafts: {},
          createdDrafts: {},
        };
      }
    },
    // First-write-wins (explicit createThread + stream reconciliation both call this).
    midasSessionSetThread(state, action: PayloadAction<string>) {
      if (state.midasSession && !state.midasSession.threadId) {
        state.midasSession.threadId = action.payload;
      }
    },
    midasSessionAppend(state, action: PayloadAction<ChatMessage>) {
      if (state.midasSession) state.midasSession.messages.push(action.payload);
    },
    midasSessionAttachDraft(state, action: PayloadAction<{ messageId: string; draft: KioskDraftTicket }>) {
      if (state.midasSession) state.midasSession.drafts[action.payload.messageId] = action.payload.draft;
    },
    midasSessionMarkCreated(state, action: PayloadAction<{ messageId: string; atIso: string }>) {
      if (state.midasSession) state.midasSession.createdDrafts[action.payload.messageId] = action.payload.atIso;
    },
    midasSessionReset(state) {
      state.midasSession = null;
    },
    // KV2-§8.3 — open a historical thread: REPLACE the session with server truth.
    midasSessionHydrate(
      state,
      action: PayloadAction<{ machineId: string; threadId: string; messages: ChatMessage[] }>
    ) {
      state.midasSession = {
        machineId: action.payload.machineId,
        threadId: action.payload.threadId,
        messages: action.payload.messages,
        drafts: {},
        createdDrafts: {},
      };
    },
    // KV2-§8.3 — "Nový chat": fresh empty session for this machine in ONE action
    // (Start won't re-run the create-thread effect; first send reconciles the id).
    midasSessionNew(state, action: PayloadAction<string>) {
      state.midasSession = {
        machineId: action.payload,
        threadId: null,
        messages: [],
        drafts: {},
        createdDrafts: {},
      };
    },
  },
  extraReducers: (builder) => {
    // Additive listen on the EXISTING public thunks — publicSlice itself is untouched.
    // fulfilled extends the KV2-0 lastLoadedAt stamp with loadedKey; rejected is NEW.
    // Scope key = action.meta.arg per thunk (see the settle helpers above).
    builder
      .addCase(getMachine.fulfilled, (state, action) => {
        settleFulfilled(state, "machine", action.meta.arg); // arg = internalNumber
      })
      .addCase(getMachine.rejected, (state, action) => {
        settleRejected(state, "machine", action.meta.arg);
      })
      .addCase(getCustomer.fulfilled, (state, action) => {
        settleFulfilled(state, "customer", action.meta.arg); // arg = customerId GUID
      })
      .addCase(getCustomer.rejected, (state, action) => {
        settleRejected(state, "customer", action.meta.arg);
      })
      .addCase(getServiceReports.fulfilled, (state, action) => {
        settleFulfilled(state, "serviceReports", action.meta.arg?.machineNumber);
      })
      .addCase(getServiceReports.rejected, (state, action) => {
        settleRejected(state, "serviceReports", action.meta.arg?.machineNumber);
      })
      .addCase(getServiceReport.fulfilled, (state, action) => {
        settleFulfilled(state, "serviceReport", action.meta.arg); // arg = report number
      })
      .addCase(getServiceReport.rejected, (state, action) => {
        settleRejected(state, "serviceReport", action.meta.arg);
      })
      .addCase(getDocuments.fulfilled, (state, action) => {
        settleFulfilled(state, "documents", action.meta.arg); // arg = machineNumber
      })
      .addCase(getDocuments.rejected, (state, action) => {
        settleRejected(state, "documents", action.meta.arg);
      })
      // ── KV2-7 task surfaces (additive) ──
      // Tasks list keyed on machineId (GUID); detail keyed on the WO number. A successful
      // action delivers fresh detail → settle "task" fulfilled (flips a cached-dimmed
      // detail back to normal). Action REJECTIONS do NOT settle — a failed button press
      // is not a load failure; it renders in the sheet/inline (§9.6), not the surface.
      .addCase(getKioskTasks.fulfilled, (state, action) => {
        settleFulfilled(state, "tasks", action.meta.arg.machineId);
      })
      .addCase(getKioskTasks.rejected, (state, action) => {
        settleRejected(state, "tasks", action.meta.arg.machineId);
      })
      .addCase(getKioskTask.fulfilled, (state, action) => {
        settleFulfilled(state, "task", action.meta.arg.number);
      })
      .addCase(getKioskTask.rejected, (state, action) => {
        settleRejected(state, "task", action.meta.arg.number);
      })
      .addCase(startKioskTask.fulfilled, (state, action) => {
        settleFulfilled(state, "task", action.meta.arg.number);
      })
      .addCase(holdKioskTask.fulfilled, (state, action) => {
        settleFulfilled(state, "task", action.meta.arg.number);
      })
      .addCase(resumeKioskTask.fulfilled, (state, action) => {
        settleFulfilled(state, "task", action.meta.arg.number);
      })
      .addCase(doneKioskTask.fulfilled, (state, action) => {
        settleFulfilled(state, "task", action.meta.arg.number);
      });
  },
});

export const {
  setTier,
  openAccountSheet,
  closeAccountSheet,
  setOffline,
  setValidationSummary,
  midasSessionStart,
  midasSessionSetThread,
  midasSessionAppend,
  midasSessionAttachDraft,
  midasSessionMarkCreated,
  midasSessionReset,
  midasSessionHydrate,
  midasSessionNew,
} = kioskV2Slice.actions;

export default kioskV2Slice.reducer;
